import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchContactEnquiries } from '../services/contactEnquiryService';

const limitOptions = [10, 20, 50];

const ContactEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data } = await fetchContactEnquiries({
          page,
          limit,
          search: search ? search : undefined,
        });

        setEnquiries(data?.data || []);
        setTotal(data?.pagination?.total || 0);
        setTotalPages(data?.pagination?.totalPages || 0);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load contact enquiries');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [page, limit, search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handlePrev = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  const rangeLabel = useMemo(() => {
    if (!total) {
      return 'No enquiries yet';
    }
    const start = (page - 1) * limit + 1;
    const end = Math.min(start + enquiries.length - 1, total);
    return `Showing ${start}-${end} of ${total}`;
  }, [enquiries.length, limit, page, total]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    try {
      return new Intl.DateTimeFormat('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    } catch (error) {
      return value;
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Contact Enquiries</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Leads submitted from the DialTPR customer contact form.
            </p>
          </div>
          <form onSubmit={handleSearchSubmit} className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, email or phone"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500"
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-xl dark:bg-gray-800 dark:ring-white/10">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700">
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span>{rangeLabel}</span>
              <label className="flex items-center gap-2">
                <span className="hidden text-xs text-gray-500 sm:inline dark:text-gray-400">Rows per page</span>
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  {limitOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={page <= 1 || loading}
                className={`rounded-md border px-3 py-1 text-sm font-medium transition ${
                  page <= 1 || loading
                    ? 'cursor-not-allowed border-gray-200 text-gray-300 dark:border-gray-700 dark:text-gray-600'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {totalPages ? page : 0} of {totalPages}
              </span>
              <button
                onClick={handleNext}
                disabled={page >= totalPages || loading}
                className={`rounded-md border px-3 py-1 text-sm font-medium transition ${
                  page >= totalPages || loading
                    ? 'cursor-not-allowed border-gray-200 text-gray-300 dark:border-gray-700 dark:text-gray-600'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">#</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Name</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Message</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-16 text-center text-gray-500 dark:text-gray-300">
                      Loading enquiries...
                    </td>
                  </tr>
                ) : enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-16 text-center text-gray-500 dark:text-gray-300">
                      No enquiries found.
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enquiry, index) => (
                    <tr key={enquiry.id || index} className="bg-white odd:bg-white even:bg-gray-50 dark:bg-gray-800 dark:odd:bg-gray-800 dark:even:bg-gray-900/40">
                      <td className="px-4 py-3 align-top text-gray-500 dark:text-gray-300">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td className="px-4 py-3 align-top text-gray-900 dark:text-gray-100">
                        <div className="font-medium">{enquiry.name || '-'}</div>
                        {enquiry.source && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">{enquiry.source}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200">
                        <div className="space-y-1">
                          {enquiry.email && <div>{enquiry.email}</div>}
                          {enquiry.phone && <div>{enquiry.phone}</div>}
                          {!enquiry.email && !enquiry.phone && <div className="text-gray-400 dark:text-gray-500">—</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200">
                        <p className="max-w-xs whitespace-pre-wrap text-sm leading-relaxed">
                          {enquiry.message}
                        </p>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300">
                        {formatDateTime(enquiry.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </LayoutWithSidebar>
  );
};

export default ContactEnquiries;
